import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Separator } from "./ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Upload, Plus, Package, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import {
  Endgood,
  EndgoodItemType,
  ENDGOOD_ITEM_TYPE_OPTIONS,
  endgoodApiService,
  formatEndgoodItemTypeLabel,
  normalizeEndgoodItemType,
} from '../services/endgoodApi';
import { HsnCode, hsnApiService } from '../services/hsnApi';
import { CreateHsnDialog } from './CreateHsnDialog';
import { EndgoodCategory, endgoodCategoryApiService } from '../services/endgoodCategoryApi';
import { fileUploadApiService, FileUploadResponse } from '../services/fileUploadApi';
import { getMasterPagesConfig } from '../config/uiConfig';

interface EndgoodFormData {
  endgoodName: string;
  partNumber: string;
  sku: string;
  description: string;
  drawingNumber: string;
  drawingImagePath: string;
  productionWeight: number;
  itemType: EndgoodItemType;
  uom: string;
  isActive: boolean;
  hsnId: number;
  endgoodCategoryId: number;
}

interface UploadedFile {
  id: number;
  fileName: string;
  originalName: string;
  originalFileName?: string; // Server response field
  filePath: string;
  fileKey?: string; // Server response field
  fileSize: number;
  mimeType: string;
  contentType?: string; // Server response field
  uploadedAt: string;
  thumbnailUrl?: string;
  fileUrl?: string; // Server response field
  thumbnailKey?: string; // Server response field
}

interface EndgoodFormProps {
  part?: Endgood | null;
  isViewMode?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
}

export function EndgoodForm({ part, isViewMode = false, onSave, onCancel }: EndgoodFormProps) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [currentPart, setCurrentPart] = useState<Endgood | null>(part || null);
  const [currentIsViewMode, setCurrentIsViewMode] = useState(isViewMode);
  // Note: We store productionWeight in GRAMS in the UI and convert to/from kg for the API
  const [formData, setFormData] = useState<EndgoodFormData>({
    endgoodName: '',
    partNumber: '',
    sku: '',
    description: '',
    drawingNumber: '',
    drawingImagePath: '',
    productionWeight: 0,
    itemType: 'GOODS',
    uom: 'NOS',
    isActive: true,
    hsnId: 0,
    endgoodCategoryId: 0
  });

  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [hsnCodes, setHsnCodes] = useState<HsnCode[]>([]);
  const [categories, setCategories] = useState<EndgoodCategory[]>([]);
  
  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [dragActive, setDragActive] = useState(false);
  const [sessionId] = useState(() => fileUploadApiService.generateSessionId());
  const [previewFile, setPreviewFile] = useState<UploadedFile | null>(null);

  // Determine if we're in view mode based on URL
  useEffect(() => {
    if (id && window.location.pathname.includes('/view')) {
      setCurrentIsViewMode(true);
    } else if (id && window.location.pathname.includes('/edit')) {
      setCurrentIsViewMode(false);
    }
  }, [id]);

  // Load part data if ID is provided
  useEffect(() => {
    if (id && !currentPart) {
      const loadPart = async () => {
        try {
          const partData = await endgoodApiService.getEndgoodById(id);
          setCurrentPart(partData);

          // Load existing files for the endgood
          await loadExistingFiles(parseInt(id));
        } catch (error) {
          // console.error('Error loading part:', error);
          toast.error('Failed to load part data');
        }
      };
      loadPart();
    }
  }, [id, currentPart]);

  // Load HSN codes and categories
  useEffect(() => {
    const loadData = async () => {
      try {
        const [hsnCodesData, categoriesData] = await Promise.all([
          hsnApiService.getHsnCodes(),
          endgoodCategoryApiService.getEndgoodCategories()
        ]);
        setHsnCodes(hsnCodesData);
        setCategories(categoriesData);
      } catch (error) {
        // console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  const handleHsnCreated = async () => {
    try {
      const updatedHsnCodes = await hsnApiService.getHsnCodes();
      setHsnCodes(updatedHsnCodes);
    } catch (error) {
      // console.error('Error refreshing HSN codes:', error);
    }
  };

  const handleCategoryCreated = async () => {
    try {
      const updatedCategories = await endgoodCategoryApiService.getEndgoodCategories();
      setCategories(updatedCategories);
    } catch (error) {
      // console.error('Error refreshing categories:', error);
    }
  };

  // Populate form when part is provided
  useEffect(() => {
    if (currentPart) {
      setFormData({
        endgoodName: currentPart.endgoodName || '',
        partNumber: currentPart.partNumber || '',
        sku: currentPart.sku || '',
        description: currentPart.description || '',
        drawingNumber: currentPart.drawingNumber || '',
        drawingImagePath: currentPart.drawingImagePath || '',
        productionWeight: currentPart.productionWeight || 0,
        itemType: normalizeEndgoodItemType(currentPart.itemType),
        uom: currentPart.uom?.trim() || 'NOS',
        isActive: currentPart.isActive,
        hsnId: currentPart.hsn?.id || 0,
        endgoodCategoryId: currentPart.endgoodCategory?.id || 0
      });
    }
  }, [currentPart]);

  const handleInputChange = (field: keyof EndgoodFormData, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    if (files.length === 0) return;

    // Prevent multiple simultaneous uploads
    if (uploadingFiles) {
      // console.log('⚠️ Upload already in progress, ignoring new file selection');
      return;
    }

    // Check if category is selected (mandatory for file upload)
    if (!formData.endgoodCategoryId || formData.endgoodCategoryId === 0) {
      toast.error('Please select an endgood category before uploading files');
      return;
    }

    // Get the selected category name
    const selectedCategory = categories.find(cat => cat.id === formData.endgoodCategoryId);
    if (!selectedCategory) {
      toast.error('Selected category not found');
      return;
    }

    // Construct folder path: 'endgood/<categoryName>'
    const folderPath = `endgood/${selectedCategory.categoryName}`;

    // Validate file sizes (10MB limit)
    const oversizedFiles = files.filter(file => file.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      toast.error(`Files exceed 10MB limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
      return;
    }

    // Validate file types
    const allowedTypes = ['.pdf', '.dwg', '.dxf', '.jpg', '.jpeg', '.png'];
    const invalidFiles = files.filter(file => {
      const extension = '.' + file.name.split('.').pop()?.toLowerCase();
      return !allowedTypes.includes(extension);
    });
    
    if (invalidFiles.length > 0) {
      toast.error(`Invalid file types: ${invalidFiles.map(f => f.name).join(', ')}`);
      return;
    }

    setUploadingFiles(true);
    setUploadProgress({});

    try {
      // Upload files with real progress tracking using presigned URLs
      const uploadPromises = files.map(async (file, index) => {
        const fileKey = `${file.name}-${index}`;
        
        try {
          const result = await fileUploadApiService.uploadFile(
            file, 
            'drawing',
            (progress) => {
              setUploadProgress(prev => ({
                ...prev,
                [fileKey]: progress
              }));
            },
            folderPath // Pass the constructed folder path
          );
          return result;
        } catch (error) {
          // Set progress to 0 on error
          setUploadProgress(prev => ({
            ...prev,
            [fileKey]: 0
          }));
          throw error;
        }
      });

      const results = await Promise.all(uploadPromises);
      
      // Add uploaded files to state
      setUploadedFiles(prev => [...prev, ...results]);
      
      // Update form data with file paths (comma-separated)
      const filePaths = results.map(file => file.filePath).join(',');
      setFormData(prev => ({
        ...prev,
        drawingImagePath: filePaths
      }));

      // Clear the file input
      event.target.value = '';
      
      // Clear progress after a delay
      setTimeout(() => {
        setUploadProgress({});
      }, 1000);

    } catch (error) {
      // console.error('Error uploading files:', error);
      toast.error('Failed to upload some files');
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleRemoveFile = async (fileId: number) => {
    try {
      if (currentPart?.id) {
        // Remove file from endgood
        await fileUploadApiService.removeFileFromEndgood(currentPart.id, fileId);
      } else {
        // For new endgoods, just remove from local state
        await fileUploadApiService.deleteFile(fileId);
      }
      
      setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
      
      // Update form data
      const remainingPaths = uploadedFiles
        .filter(file => file.id !== fileId)
        .map(file => file.filePath)
        .join(',');
      
      setFormData(prev => ({
        ...prev,
        drawingImagePath: remainingPaths
      }));
    } catch (error) {
      // console.error('Error removing file:', error);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!currentIsViewMode && !uploadingFiles) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (currentIsViewMode || uploadingFiles) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Create a synthetic event to reuse existing file handling logic
      const syntheticEvent = {
        target: {
          files: files,
          value: ''
        }
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      
      handleFileChange(syntheticEvent);
    }
  };

  // Load existing files when editing an endgood
  const loadExistingFiles = async (endgoodId: number) => {
    try {
      const files = await fileUploadApiService.getFilesByEndgood(endgoodId);
      setUploadedFiles(files);
      
      // Update form data with existing file paths
      const filePaths = files.map(file => file.fileUrl || file.fileKey).join(',');
      setFormData(prev => ({
        ...prev,
        drawingImagePath: filePaths
      }));
    } catch (error) {
      // console.error('Error loading existing files:', error);
    }
  };

  // Cleanup temporary files on component unmount
  useEffect(() => {
    return () => {
      if (uploadedFiles.length > 0) {
        const fileKeys = uploadedFiles.map(file => file.filePath);
        fileUploadApiService.cleanupTempFiles(fileKeys, sessionId);
      }
    };
  }, [uploadedFiles, sessionId]);

  const handleAddCategory = async () => {
    if (newCategory.trim()) {
      try {
        const newEndgoodCategoryData = {
          categoryName: newCategory.trim(),
          description: '',
          isActive: true,
          createdBy: 1,
          updatedBy: 1
        };

        await endgoodCategoryApiService.createEndgoodCategory(newEndgoodCategoryData);
        
        // Refresh categories after adding
        await handleCategoryCreated();
        
        setNewCategory('');
        setIsAddCategoryOpen(false);
        toast.success('Category added successfully!');
      } catch (error) {
        // console.error('Error adding category:', error);
        toast.error('Failed to add category');
      }
    }
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Validate required fields
    if (!formData.endgoodName || !formData.partNumber || !formData.endgoodCategoryId || formData.endgoodCategoryId === 0) {
      toast.error('Please fill in all required fields including Endgood Category');
      return;
    }

    setLoading(true);
    try {
      // SKU is auto-generated by backend and never sent in the payload
      const partData = {
        endgoodName: formData.endgoodName,
        partNumber: formData.partNumber,
        description: formData.description,
        drawingNumber: formData.drawingNumber,
        drawingImagePath: formData.drawingImagePath,
        productionWeight: formData.productionWeight,
        itemType: formData.itemType,
        uom: formData.uom.trim() || 'NOS',
        isActive: formData.isActive,
        hsnId: formData.hsnId,
        endgoodCategoryId: formData.endgoodCategoryId,
        createdBy: 1,
        updatedBy: 1
      };

      if (currentPart && currentPart.id) {
        // Update existing part
        await endgoodApiService.updateEndgood(currentPart.id.toString(), {
          ...partData,
          id: currentPart.id
        });

        // Handle file attachments for existing endgood
        try {
          if (uploadedFiles.length > 0) {
            const fileIds = uploadedFiles.map(file => file.id);
            await fileUploadApiService.attachFilesToEndgood(currentPart.id, fileIds, 'IMAGE');
            toast.success(`${uploadedFiles.length} file(s) attached to endgood successfully.`);
          }
        } catch (fileError) {
          // console.error('Error attaching files:', fileError);
          toast.warning('Endgood updated but failed to attach files');
        }
      } else {
        // Create new part
        const createdEndgood = await endgoodApiService.createEndgood(partData);
        toast.success('Engood created successfully!');

        // Handle file attachments for new endgood
        try {
          if (uploadedFiles.length > 0 && createdEndgood?.id) {
            const fileIds = uploadedFiles.map(file => file.id);
            await fileUploadApiService.attachFilesToEndgood(createdEndgood.id, fileIds, 'IMAGE');
            toast.success(`${uploadedFiles.length} file(s) attached to endgood successfully.`);
          }
        } catch (fileError) {
          // console.error('Error attaching files:', fileError);
          toast.warning('Endgood created but failed to attach files');
        }
      }
      
      // Navigate back to listing page
      navigate('/endgoods');
    } catch (error) {
      // console.error('Error saving part:', error);
      toast.error('Failed to save engood');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/endgoods');
  };

  const getSelectedHsn = () => {
    return hsnCodes.find(hsn => hsn.id === formData.hsnId);
  };

  return (
    <>
      <Card className="w-full border-0 shadow-lg bg-white/70 backdrop-blur-sm">
      <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-t-lg">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="text-white hover:bg-primary-600"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <CardTitle className="text-xl">
              {currentIsViewMode ? 'View Endgood' : currentPart ? 'Edit Endgood' : 'Add New Endgood'}
            </CardTitle>
            <CardDescription className="text-primary-100">
              {currentIsViewMode ? 'View endgood details' : currentPart ? 'Update endgood information' : 'Create a new endgood'}
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-6">
        <form onSubmit={handleSave} className="space-y-8">
          {/* Basic Information Section */}
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
                Basic Information
              </h3>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="endgoodName" className="text-gray-700">
                  Endgood Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="endgoodName"
                  value={formData.endgoodName}
                  onChange={(e) => handleInputChange('endgoodName', e.target.value)}
                  placeholder="Enter endgood name"
                  required
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={currentIsViewMode}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="partNumber" className="text-gray-700">
                  Part Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="partNumber"
                  value={formData.partNumber}
                  onChange={(e) => handleInputChange('partNumber', e.target.value)}
                  placeholder="Enter part number"
                  required
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={currentIsViewMode}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sku" className="text-gray-700">SKU</Label>
                <Input
                  id="sku"
                  value={formData.sku}
                  onChange={(e) => handleInputChange('sku', e.target.value)}
                  placeholder="Auto-generated by system"
                  className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={true}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-gray-700">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter description"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={currentIsViewMode}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="drawingNumber" className="text-gray-700">Drawing Number</Label>
                <Input
                  id="drawingNumber"
                  value={formData.drawingNumber}
                  onChange={(e) => handleInputChange('drawingNumber', e.target.value)}
                  placeholder="Enter drawing number"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={currentIsViewMode}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="productionWeight" className="text-gray-700">Production Weight (g)</Label>
                <Input
                  id="productionWeight"
                  type="number"
                  value={formData.productionWeight}
                  onChange={(e) =>
                    handleInputChange(
                      'productionWeight',
                      e.target.value === '' ? 0 : parseFloat(e.target.value) || 0
                    )
                  }
                  placeholder="Enter production weight in grams"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={currentIsViewMode}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="uom" className="text-gray-700">UOM</Label>
                <Input
                  id="uom"
                  value={formData.uom}
                  onChange={(e) => handleInputChange('uom', e.target.value)}
                  placeholder="e.g. NOS, KG, SET"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={currentIsViewMode}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="itemType" className="text-gray-700">
                  Item Type <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.itemType}
                  onValueChange={(value) => handleInputChange('itemType', value as EndgoodItemType)}
                  disabled={currentIsViewMode}
                >
                  <SelectTrigger
                    id="itemType"
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  >
                    <SelectValue placeholder="Select item type" />
                  </SelectTrigger>
                  <SelectContent>
                    {ENDGOOD_ITEM_TYPE_OPTIONS.map((type) => (
                      <SelectItem key={type} value={type}>
                        {formatEndgoodItemTypeLabel(type)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="isActive" className="text-gray-700">Is Active</Label>
                <Select
                  value={formData.isActive ? 'true' : 'false'}
                  onValueChange={(value) => handleInputChange('isActive', value === 'true')}
                  disabled={currentIsViewMode}
                >
                  <SelectTrigger
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  >
                    <SelectValue placeholder="Select active status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="category" className="text-gray-700">
                  Endgood Category <span className="text-red-500">*</span>
                </Label>
                <div className="flex gap-2">
                  <Select
                    value={formData.endgoodCategoryId.toString()}
                    onValueChange={(value) => handleInputChange('endgoodCategoryId', parseInt(value, 10))}
                    disabled={currentIsViewMode}
                  >
                    <SelectTrigger 
                      className="flex-1 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    >
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContentWithSearch searchPlaceholder="Search categories...">
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id.toString()}>
                          {category.categoryName}
                        </SelectItem>
                      ))}
                    </SelectContentWithSearch>
                  </Select>
                  {!currentIsViewMode && (
                    <Dialog open={isAddCategoryOpen} onOpenChange={setIsAddCategoryOpen}>
                      <DialogTrigger asChild>
                        <Button type="button" size="icon" variant="outline" className="border-primary-200 hover:bg-gradient-to-r hover:from-primary-50 hover:to-primary-100 hover:border-primary-300">
                          <Plus className="h-4 w-4 text-primary-600" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="border-primary-200">
                        <DialogHeader>
                          <DialogTitle className="bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
                            Add New Category
                          </DialogTitle>
                          <DialogDescription>
                            Enter the name for the new endgood category.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="newCategory">Category Name</Label>
                            <Input
                              id="newCategory"
                              value={newCategory}
                              onChange={(e) => setNewCategory(e.target.value)}
                              placeholder="Enter category name"
                              className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button type="button" variant="outline" onClick={() => setIsAddCategoryOpen(false)} className="border-primary-200 hover:bg-primary-50">
                            Cancel
                          </Button>
                          <Button type="button" onClick={handleAddCategory} className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg">
                            Add Category
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* HSN Section */}
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
                Tax Information
              </h3>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="space-y-2">
                <Label htmlFor="hsnCode" className="text-gray-700">HSN Code</Label>
                <div className="flex gap-2">
                  <Select 
                    value={formData.hsnId.toString()} 
                    onValueChange={(value) => handleInputChange('hsnId', parseInt(value, 10))}
                    disabled={currentIsViewMode}
                  >
                    <SelectTrigger 
                      className="flex-1 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    >
                      <SelectValue placeholder="Select HSN code" />
                    </SelectTrigger>
                    <SelectContentWithSearch searchPlaceholder="Search HSN code...">
                      {hsnCodes.map((hsn) => (
                        <SelectItem key={hsn.id} value={hsn.id.toString()}>
                          {hsn.hsnCode}
                        </SelectItem>
                      ))}
                    </SelectContentWithSearch>
                  </Select>
                  {!currentIsViewMode && (
                    <CreateHsnDialog onHsnCreated={handleHsnCreated} />
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="igst" className="text-gray-700">IGST (%)</Label>
                <Input
                  id="igst"
                  value={getSelectedHsn()?.currentIgstRate || ''}
                  placeholder="Auto-filled"
                  readOnly
                  className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cgst" className="text-gray-700">CGST (%)</Label>
                <Input
                  id="cgst"
                  value={getSelectedHsn()?.currentCgstRate || ''}
                  placeholder="Auto-filled"
                  readOnly
                  className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sgst" className="text-gray-700">SGST (%)</Label>
                <Input
                  id="sgst"
                  value={getSelectedHsn()?.currentSgstRate || ''}
                  placeholder="Auto-filled"
                  readOnly
                  className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>
            </div>
          </div>

          {/* Drawing Files Section */}
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
                Drawing Files
              </h3>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="drawingFile" className="text-gray-700">Upload Drawing Files</Label>
                
                {/* Drag and Drop Zone */}
                <div
                  className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
                    dragActive
                      ? 'border-primary-400 bg-primary-50'
                      : 'border-primary-200 hover:border-primary-300'
                  } ${currentIsViewMode || uploadingFiles ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <div className="text-center">
                    <Upload className={`mx-auto h-8 w-8 mb-2 ${dragActive ? 'text-primary-600' : 'text-gray-400'}`} />
                    <p className={`text-sm ${dragActive ? 'text-primary-600' : 'text-gray-600'}`}>
                      {dragActive ? 'Drop files here' : 'Drag and drop files here or click to browse'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Supported formats: PDF, DWG, DXF, JPG, PNG (Max 10MB each)
                    </p>
                  </div>
                  
                  {/* Hidden file input */}
                  <Input
                    id="drawingFile"
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png"
                    multiple
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={currentIsViewMode || uploadingFiles}
                  />
                </div>
              </div>

              {/* Upload Progress */}
              {uploadingFiles && Object.keys(uploadProgress).length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">Uploading files...</p>
                  {Object.entries(uploadProgress).map(([fileName, progress]) => (
                    <div key={fileName} className="space-y-1">
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>{fileName.split('-')[0]}</span>
                        <span>{progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Uploaded Files List */}
              {uploadedFiles.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-gray-700">Uploaded Files ({uploadedFiles.length})</Label>
                  <div className="space-y-2">
                    {uploadedFiles.map((file) => {
                      // console.log('File object:', file);
                      return (
                      <div 
                        key={file.id} 
                        className="flex items-center justify-between p-3 bg-gradient-to-r from-primary-50 to-primary-100/50 border border-primary-200 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          {/* File Icon or Thumbnail */}
                          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center overflow-hidden">
                            {(file.fileUrl || file.thumbnailUrl) ? (
                              <img 
                                src={file.fileUrl || file.thumbnailUrl} 
                                alt={file.originalName || file.originalFileName || 'File thumbnail'}
                                className="w-full h-full object-cover"
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  objectFit: 'cover'
                                }}
                                onLoad={() => {
                                  // console.log('Thumbnail loaded successfully:', file.fileUrl || file.thumbnailUrl);
                                }}
                                onError={(e) => {
                                  console.log('Thumbnail failed to load:', {
                                    fileUrl: file.fileUrl,
                                    thumbnailUrl: file.thumbnailUrl,
                                    src: e.currentTarget.src
                                  });
                                  // Fallback to icon if thumbnail fails to load
                                  const target = e.currentTarget as HTMLImageElement;
                                  const nextElement = target.nextElementSibling as HTMLElement;
                                  target.style.display = 'none';
                                  if (nextElement) {
                                    nextElement.style.display = 'flex';
                                  }
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <span className="text-xs text-gray-500">No image</span>
                              </div>
                            )}
                            <div className={`w-full h-full flex items-center justify-center ${(file.fileUrl || file.thumbnailUrl) ? 'hidden' : 'flex'}`}>
                              <Upload className="h-4 w-4 text-primary-600" />
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{file.originalName}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(file.fileSize)} • {file.mimeType}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setPreviewFile(file)}
                            className="text-blue-600 hover:bg-blue-50 border-blue-200"
                          >
                            Preview
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(fileUploadApiService.getFileUrl(file.filePath), '_blank')}
                            className="text-green-600 hover:bg-green-50 border-green-200"
                          >
                            Download
                          </Button>
                          {!currentIsViewMode && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemoveFile(file.id)}
                              className="text-red-600 hover:bg-red-50 border-red-200"
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Audit Information Section - Only shown in view mode */}
          {currentIsViewMode && currentPart && (
            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
                  Audit Information
                </h3>
                <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-gray-700">Created By</Label>
                  <Input
                    value={currentPart.createdBy || 'N/A'}
                    readOnly
                    className="bg-gray-50 border-gray-200"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700">Updated By</Label>
                  <Input
                    value={currentPart.updatedBy || 'N/A'}
                    readOnly
                    className="bg-gray-50 border-gray-200"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700">Created At</Label>
                  <Input
                    value={currentPart.createdAt ? new Date(currentPart.createdAt).toLocaleString() : 'N/A'}
                    readOnly
                    className="bg-gray-50 border-gray-200"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700">Updated At</Label>
                  <Input
                    value={currentPart.updatedAt ? new Date(currentPart.updatedAt).toLocaleString() : 'N/A'}
                    readOnly
                    className="bg-gray-50 border-gray-200"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {!currentIsViewMode && (
            <div className="flex justify-end gap-4 pt-6 border-t border-primary-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="border-primary-200 text-primary-600 hover:bg-primary-50"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
                disabled={loading}
              >
                {loading ? 'Saving...' : (currentPart ? 'Update Endgood' : 'Create Endgood')}
              </Button>
            </div>
          )}

          {currentIsViewMode && (
            <div className="flex justify-end gap-4 pt-6 border-t border-primary-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="border-primary-200 text-primary-600 hover:bg-primary-50"
              >
                Close
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>

    {/* File Preview Modal */}
    {previewFile && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full mx-4 overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold">{previewFile.originalName}</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPreviewFile(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </Button>
          </div>
          <div className="p-4 max-h-[calc(90vh-80px)] overflow-auto">
            {(previewFile.mimeType || previewFile.contentType)?.startsWith('image/') ? (
              <img
                src={fileUploadApiService.getFileUrl(previewFile.filePath || previewFile.fileKey || '')}
                alt={previewFile.originalName || previewFile.originalFileName || 'Preview'}
                className="max-w-full h-auto mx-auto"
              />
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Upload className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-600 mb-4">Preview not available for this file type</p>
                <Button
                  onClick={() => window.open(fileUploadApiService.getFileUrl(previewFile.filePath || previewFile.fileKey || ''), '_blank')}
                  className="bg-primary-600 hover:bg-primary-700"
                >
                  Open File
                </Button>
              </div>
            )}
          </div>
          <div className="p-4 border-t bg-gray-50">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>{formatFileSize(previewFile.fileSize)}</span>
              <span>{previewFile.mimeType || previewFile.contentType}</span>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
}